// Incidents: one taxonomy for the log page and for filing from a client or
// a visit. What happened (incident_type) is kept apart from what we did
// about it (category, the disciplinary ladder), because most incidents are
// not discipline and everything was landing in "Other".
import { supabase } from './supabase';

export const INCIDENT_TYPES = [
  'Client safety event',
  'Injury / accident (client)',
  'Injury / accident (staff)',
  'Medication / treatment error',
  'Abuse, neglect or exploitation allegation',
  'Elopement / wandering',
  'Hospitalisation / emergency transport',
  'Death',
  'Fall',
  'Equipment failure',
  'Safety hazard / near miss',
  'Privacy / HIPAA',
  'Policy violation',
  'Attendance / no-show',
  'Conduct / behavior',
  'Vehicle / transportation',
  'Property / equipment',
  'Complaint (external)',
  'Disciplinary action',
  'Other',
];

// Types that must be reported externally within 24 hours unless the office
// says otherwise. The tick can still be cleared by hand.
export const REPORTABLE_TYPES = new Set([
  'Client safety event',
  'Injury / accident (client)',
  'Medication / treatment error',
  'Abuse, neglect or exploitation allegation',
  'Elopement / wandering',
  'Hospitalisation / emergency transport',
  'Death',
  'Fall',
]);

// What we did about it. 'none' is the default: most incidents are not discipline.
export const ACTIONS = [
  { key: 'none',            label: 'No disciplinary action', color: 'var(--text3)', bg: 'var(--bg2)' },
  { key: 'coaching',        label: 'Coaching / retraining',  color: 'var(--info)',   bg: 'var(--info-bg)' },
  { key: 'verbal_warning',  label: 'Verbal Warning',         color: 'var(--warn)',   bg: 'var(--warn-bg)' },
  { key: 'written_warning', label: 'Written Warning',        color: 'var(--copper)', bg: 'var(--copper-bg)' },
  { key: 'suspension',      label: 'Suspension',             color: 'var(--danger)', bg: 'var(--danger-bg)' },
  { key: 'termination',     label: 'Termination',            color: 'var(--danger)', bg: 'var(--danger-bg)' },
  { key: 'other',           label: 'Other',                  color: 'var(--text3)',  bg: 'var(--bg2)' },
];
export const ACTION_INFO = Object.fromEntries(ACTIONS.map(a => [a.key, a]));
export const SUBJECTS = [['client', 'A client'], ['staff', 'A caregiver'], ['operational', 'The agency']];

// The clock runs from when the agency became aware, not from the event.
export const reportDeadline = (inc) => {
  const from = inc?.discovered_at || inc?.created_at || (inc?.incident_date ? `${inc.incident_date}T12:00:00` : null);
  return from ? new Date(new Date(from).getTime() + 24 * 3600000) : null;
};
export const reportOverdue = (inc) => {
  if (!inc?.reportable || inc.reported_external || inc.status === 'void') return false;
  const d = reportDeadline(inc);
  return !!d && d < new Date();
};
export const hoursLeftToReport = (inc) => {
  const d = reportDeadline(inc);
  return d ? Math.round((d - new Date()) / 3600000) : null;
};

export const nextReference = async () => {
  const { data } = await supabase.from('staff_incidents').select('reference_no').not('reference_no', 'is', null);
  const max = (data || []).reduce((m, r) => Math.max(m, parseInt(String(r.reference_no).replace(/\D/g, ''), 10) || 0), 0);
  return `INC-${String(max + 1).padStart(5, '0')}`;
};

// One incident can name both a caregiver and a client; subject_type only says
// which one it is mainly about.
export const fileIncident = async (form, profile) => {
  if (!form.incident_type) throw new Error('Choose what happened.');
  if (!String(form.description || '').trim()) throw new Error('Describe what happened.');
  if (form.subject_type === 'staff' && !form.staff_id) throw new Error('Name the caregiver this is mainly about.');
  if (form.subject_type === 'client' && !form.client_id) throw new Error('Name the client this is mainly about.');
  const row = {
    reference_no: await nextReference(),
    subject_type: form.subject_type,
    staff_id: form.staff_id || null,
    client_id: form.client_id || null,
    incident_date: form.incident_date,
    discovered_at: form.discovered_at ? new Date(form.discovered_at).toISOString() : new Date().toISOString(),
    incident_type: form.incident_type,
    category: form.category || 'none',
    description: form.description.trim(),
    action_taken: (form.action_taken || '').trim() || null,
    follow_up_date: form.follow_up_date || null,
    reportable: !!form.reportable,
    reported_by: profile?.full_name || null,
    status: 'open',
    source: form.source || 'manual',
  };
  const { data, error } = await supabase.from('staff_incidents').insert([row]).select().single();
  if (error) throw new Error(error.message);
  return data;
};

export const getOverdueExternalReports = () =>
  supabase.from('staff_incidents')
    .select('id, reference_no, incident_date, discovered_at, created_at, incident_type, reportable, reported_external, status')
    .eq('reportable', true).eq('reported_external', false).neq('status', 'void');
